import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 30, bottom: 50, left: 55 };
const CHART_HEIGHT = 350;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  statLabel: {
    color: '#9ca3af',
  },
  statValue: {
    fontWeight: 600,
    color: '#1f2937',
  },
  significanceBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    background: '#fef3c7',
    color: '#92400e',
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
  },
};

export default function EventStudyChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.averageAR || data.averageAR.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', CHART_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const windowSize = data.windowSize || 3;
    const periods = data.averageAR.map((_, i) => i - windowSize);

    // X scale — band for bars, with a linear overlay for lines
    const x = d3.scaleBand()
      .domain(periods)
      .range([0, innerW])
      .padding(0.3);

    const xLinear = d3.scaleLinear()
      .domain([periods[0] - 0.5, periods[periods.length - 1] + 0.5])
      .range([0, innerW]);

    // Y domain: consider all AR and CAR values
    const allValues = [
      ...data.averageAR,
      ...data.averageCAR,
    ];

    if (data.events) {
      data.events.forEach((evt) => {
        if (evt.cumulativeAR) allValues.push(...evt.cumulativeAR);
      });
    }

    const yMin = d3.min(allValues);
    const yMax = d3.max(allValues);
    const yPadding = Math.max((yMax - yMin) * 0.15, 0.3);

    const y = d3.scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks(6);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Horizontal zero line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', y(0))
      .attr('y2', y(0))
      .attr('stroke', '#9ca3af')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '6,4');

    // Vertical dashed line at t=0
    const t0X = x(0) + x.bandwidth() / 2;
    g.append('line')
      .attr('x1', t0X)
      .attr('x2', t0X)
      .attr('y1', 0)
      .attr('y2', innerH)
      .attr('stroke', '#6b7280')
      .attr('stroke-width', 1.5)
      .attr('stroke-dasharray', '6,3');

    // "Event" label at top of t=0 line
    g.append('text')
      .attr('x', t0X)
      .attr('y', -6)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('font-weight', 600)
      .attr('fill', '#6b7280')
      .text('Event');

    // Individual event CAR lines (thin, semi-transparent background)
    if (data.events && data.events.length > 0) {
      const lineGen = d3.line()
        .x((d, i) => xLinear(periods[i]))
        .y((d) => y(d))
        .defined((d) => d != null)
        .curve(d3.curveMonotoneX);

      data.events.forEach((evt) => {
        if (!evt.cumulativeAR) return;
        g.append('path')
          .datum(evt.cumulativeAR)
          .attr('d', lineGen)
          .attr('fill', 'none')
          .attr('stroke', '#93c5fd')
          .attr('stroke-width', 1)
          .attr('stroke-opacity', 0.25);
      });
    }

    // Bar chart of average abnormal returns
    periods.forEach((period, i) => {
      const arValue = data.averageAR[i];
      if (arValue == null) return;

      const isEventDay = period === 0;
      const barColor = isEventDay
        ? '#f59e0b'
        : arValue >= 0
          ? '#3b82f6'
          : '#ef4444';

      const barY = arValue >= 0 ? y(arValue) : y(0);
      const barH = Math.abs(y(0) - y(arValue));

      g.append('rect')
        .attr('x', x(period))
        .attr('y', barY)
        .attr('width', x.bandwidth())
        .attr('height', Math.max(1, barH))
        .attr('fill', barColor)
        .attr('opacity', isEventDay ? 1 : 0.7)
        .attr('rx', 2);

      // Highlight border on event day bar
      if (isEventDay) {
        g.append('rect')
          .attr('x', x(period))
          .attr('y', barY)
          .attr('width', x.bandwidth())
          .attr('height', Math.max(1, barH))
          .attr('fill', 'none')
          .attr('stroke', '#d97706')
          .attr('stroke-width', 2)
          .attr('rx', 2);
      }
    });

    // Overlay: cumulative abnormal returns as a line
    const carLineGen = d3.line()
      .x((d, i) => xLinear(periods[i]))
      .y((d) => y(d))
      .defined((d) => d != null)
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(data.averageCAR)
      .attr('d', carLineGen)
      .attr('fill', 'none')
      .attr('stroke', '#1f2937')
      .attr('stroke-width', 2.5);

    // Dots on CAR line
    periods.forEach((period, i) => {
      const carValue = data.averageCAR[i];
      if (carValue == null) return;

      g.append('circle')
        .attr('cx', xLinear(period))
        .attr('cy', y(carValue))
        .attr('r', period === 0 ? 5 : 3)
        .attr('fill', period === 0 ? '#f59e0b' : '#1f2937')
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5);
    });

    // Significance star at event day
    if (data.significantAtEvent) {
      const carAtEvent = data.averageCAR[windowSize];
      if (carAtEvent != null) {
        g.append('text')
          .attr('x', xLinear(0))
          .attr('y', y(carAtEvent) - 14)
          .attr('text-anchor', 'middle')
          .attr('font-size', '16px')
          .attr('fill', '#f59e0b')
          .text('\u2605');
      }
    }

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .tickSize(0)
          .tickFormat((d) => `t${d >= 0 ? '+' : ''}${d}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', (d) => (d === 0 ? '#f59e0b' : '#6b7280'))
      .attr('font-weight', (d) => (d === 0 ? 600 : 400));

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 38)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#9ca3af')
      .text('Periods relative to event');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(6)
          .tickSize(0)
          .tickFormat((v) => {
            const sign = v > 0 ? '+' : '';
            return `${sign}${v.toFixed(1)}`;
          })
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Invisible overlay rectangles for hover per period
    periods.forEach((period, i) => {
      g.append('rect')
        .attr('x', x(period))
        .attr('y', 0)
        .attr('width', x.bandwidth())
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            period,
            ar: data.averageAR[i],
            car: data.averageCAR[i],
            eventCount: data.events ? data.events.length : 0,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.averageAR || data.averageAR.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Event Study Analysis</div>
          <div style={styles.subtitle}>Abnormal price behavior around key events</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No event study data available
        </div>
      </div>
    );
  }

  const windowSize = data.windowSize || 3;
  const carAtEvent = data.averageCAR ? data.averageCAR[windowSize] : null;

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Event Study Analysis</div>
        <div style={styles.subtitle}>Abnormal price behavior around key events</div>
      </div>

      {/* Summary stats */}
      <div style={styles.statsRow}>
        {data.tStatistic != null && (
          <div style={styles.statItem}>
            <span style={styles.statLabel}>t-statistic:</span>
            <span style={styles.statValue}>{data.tStatistic.toFixed(2)}</span>
          </div>
        )}
        {data.pValue != null && (
          <div style={styles.statItem}>
            <span style={styles.statLabel}>p-value:</span>
            <span style={styles.statValue}>{data.pValue.toFixed(3)}</span>
          </div>
        )}
        {carAtEvent != null && (
          <div style={styles.statItem}>
            <span style={styles.statLabel}>CAR at event:</span>
            <span style={{
              ...styles.statValue,
              color: carAtEvent >= 0 ? '#16a34a' : '#dc2626',
            }}>
              {carAtEvent >= 0 ? '+' : ''}{carAtEvent.toFixed(2)}
            </span>
          </div>
        )}
        {data.events && (
          <div style={styles.statItem}>
            <span style={styles.statLabel}>Events:</span>
            <span style={styles.statValue}>{data.events.length}</span>
          </div>
        )}
        {data.significantAtEvent && (
          <div style={styles.significanceBadge}>
            <span>{'\u2605'}</span>
            <span>Significant</span>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '14px',
        marginBottom: '12px',
        fontSize: '12px',
        color: '#6b7280',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width="14" height="10">
            <rect width="14" height="10" fill="#3b82f6" opacity="0.7" rx="1" />
          </svg>
          <span>Avg AR</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width="14" height="10">
            <rect width="14" height="10" fill="#f59e0b" rx="1" />
          </svg>
          <span>AR at t=0</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#1f2937" strokeWidth="2.5" />
          </svg>
          <span style={{ fontWeight: 600, color: '#1f2937' }}>Avg CAR</span>
        </div>
        {data.events && data.events.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <svg width="20" height="10">
              <line x1="0" y1="5" x2="20" y2="5" stroke="#93c5fd" strokeWidth="1" strokeOpacity="0.5" />
            </svg>
            <span>Individual CARs</span>
          </div>
        )}
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '6px' }}>
            Period: t{tooltip.period >= 0 ? '+' : ''}{tooltip.period}
            {tooltip.period === 0 ? ' (Event Day)' : ''}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Abnormal Return</span>
            <span style={{
              fontWeight: 600,
              color: tooltip.ar >= 0 ? '#16a34a' : '#dc2626',
            }}>
              {tooltip.ar >= 0 ? '+' : ''}{tooltip.ar.toFixed(3)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Cumulative AR</span>
            <span style={{
              fontWeight: 600,
              color: tooltip.car >= 0 ? '#16a34a' : '#dc2626',
            }}>
              {tooltip.car >= 0 ? '+' : ''}{tooltip.car.toFixed(3)}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#6b7280' }}>Events</span>
            <span style={{ fontWeight: 600 }}>{tooltip.eventCount}</span>
          </div>
        </div>
      )}
    </div>
  );
}
